import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import WizardWrapper from './WizardWrapper';
import { Box, Button, Heading } from '@zillow/constellation-10';
import OverviewScreen from './enrollmentScreens/OverviewScreen';
import PrivacyNoticeScreen from './enrollmentScreens/PrivacyNoticeScreen';
import { useRentReportingAuth } from '../contexts/RentReportingAuthProvider';
import { EnrollmentProvider, ReportingForAllEnrollmentStatus } from '@zg-rentals/ts-rent-credit-report-client';
import {
  ClickstreamAuthTriggerSource,
  ClickstreamTriggerObjectOnboardingScreen,
  emitAuthStartEvent,
  emitOnboardingWizardViewEvent,
} from '../analytics/events';
import type { WizardScreen } from './WizardWrapper';
import { handleExitNavigation } from '../utils/exitHandler';

export const EnrollmentScreenWrapper = ({ children }: { children: React.ReactNode }) => {
  return <Box css={{ display: 'flex', flexDirection: 'column', gap: '{spacing.800}' }}>{children}</Box>;
};

export default function EnrollmentPage() {
  const router = useRouter();
  const { user, login, enrollmentMutation } = useRentReportingAuth();
  const hasEmittedViewEvent = useRef(false);
  const [hasEnrollmentError, setHasEnrollmentError] = useState(false);

  useEffect(() => {
    if (!hasEmittedViewEvent.current) {
      hasEmittedViewEvent.current = true;
      emitOnboardingWizardViewEvent();
    }
  }, []);

  // Check for enrollment error from query params (from EnrollmentRedirectHandler)
  useEffect(() => {
    if (router.query.enrollment_error === 'true') {
      setHasEnrollmentError(true);
      // Clean up the query param
      const { enrollment_error: _enrollment_error, ...restQuery } = router.query;
      router.replace(
        {
          pathname: router.pathname,
          query: restQuery,
        },
        undefined,
        { shallow: true },
      );
    }
  }, [router]);

  const handlePrivacyNoticeNext = useCallback(async () => {
    enrollmentMutation.reset();
    setHasEnrollmentError(false);

    if (user?.isLoggedIn) {
      try {
        await enrollmentMutation.mutateAsync({
          postData: {
            enrollmentProvider: EnrollmentProvider.ESUSU,
            enrollmentStatus: ReportingForAllEnrollmentStatus.INITIATED_ENROLLMENT,
          },
        });
        const { ignoreEnrollment: _ignoreEnrollment, ...restQuery } = router.query;
        router.replace(
          {
            pathname: router.pathname,
            query: restQuery,
          },
          undefined,
          { shallow: true },
        );
      } catch (error) {
        // Error is automatically captured by React Query
      }
    } else {
      emitAuthStartEvent(ClickstreamAuthTriggerSource.ESUSU_ENROLLMENT);
      login(ClickstreamAuthTriggerSource.ESUSU_ENROLLMENT, 'initiate_enrollment');
    }
  }, [user?.isLoggedIn, enrollmentMutation, login]);

  const handleExitClick = useCallback(() => {
    handleExitNavigation(router.query);
  }, [router.query]);

  const screens: Array<WizardScreen> = useMemo(
    () => [
      {
        id: 'overview',
        body: (
          <EnrollmentScreenWrapper>
            <OverviewScreen />
          </EnrollmentScreenWrapper>
        ),
        nextButtonText: 'Continue',
        clickstreamTriggerObject: ClickstreamTriggerObjectOnboardingScreen.START_PAGE,
      },
      {
        id: 'privacyNotice',
        body: (
          <EnrollmentScreenWrapper>
            <PrivacyNoticeScreen />
          </EnrollmentScreenWrapper>
        ),
        nextButtonText: 'Get started',
        nextAction: handlePrivacyNoticeNext,
        isLoading: enrollmentMutation.isPending,
        clickstreamTriggerObject: ClickstreamTriggerObjectOnboardingScreen.HOW_IT_WORKS,
      },
    ],
    [handlePrivacyNoticeNext, enrollmentMutation.isPending],
  );

  const heading = (
    <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
      <Heading level={5} fontFamily="heading" css={{ margin: 'auto 0' }}>
        CreditClimb
      </Heading>
      <Button tone="neutral" onClick={handleExitClick}>
        Exit
      </Button>
    </Box>
  );

  const errorMessage =
    enrollmentMutation.isError || hasEnrollmentError
      ? 'We encountered an error while processing your enrollment. Please try again.'
      : null;

  const handleDismissError = useCallback(() => {
    enrollmentMutation.reset();
    setHasEnrollmentError(false);
  }, [enrollmentMutation]);

  return (
    <WizardWrapper screens={screens} defaultHeader={heading} error={errorMessage} onDismissError={handleDismissError} />
  );
}
